import { Trans } from "@lingui/react/macro";
import { createFileRoute, Link } from "@tanstack/react-router";
import { LinkIcon, PlusIcon } from "lucide-react";
import { useEffect } from "react";

import { NoActiveThreadState } from "../components/NoActiveThreadState";
import { Button } from "../components/ui/button";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "../components/ui/empty";
import { SidebarInset, SidebarTrigger } from "../components/ui/sidebar";
import { usePrimaryEnvironmentId } from "../environments/primary";
import { useSavedEnvironmentRegistryStore } from "../environments/runtime";
import { useOpenHomeDraft } from "../hooks/useHomeDraft";
import { selectEnvironmentState, useStore } from "../store";
import { APP_DISPLAY_NAME } from "~/branding";

function ChatIndexRouteView() {
  const { authGateState } = Route.useRouteContext();
  const savedEnvironmentCount = useSavedEnvironmentRegistryStore(
    (state) => Object.keys(state.byId).length,
  );
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const canOpenHomeDraft = useStore((state) => {
    if (!primaryEnvironmentId) {
      return false;
    }
    const environmentState = selectEnvironmentState(state, primaryEnvironmentId);
    return environmentState.bootstrapComplete && environmentState.projectIds.length > 0;
  });
  const openHomeDraft = useOpenHomeDraft();

  useEffect(() => {
    if (!primaryEnvironmentId || !canOpenHomeDraft) {
      return;
    }
    void openHomeDraft(primaryEnvironmentId, { replace: true });
  }, [canOpenHomeDraft, openHomeDraft, primaryEnvironmentId]);

  if (authGateState.status === "hosted-static" && savedEnvironmentCount === 0) {
    return <HostedStaticOnboardingState />;
  }

  return <NoActiveThreadState />;
}

export const Route = createFileRoute("/_chat/")({
  component: ChatIndexRouteView,
});

function HostedStaticOnboardingState() {
  return (
    <SidebarInset className="h-dvh min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden bg-background">
        <header className="border-b border-border px-3 py-2 sm:px-5 sm:py-3">
          <div className="flex items-center gap-2">
            <SidebarTrigger className="size-7 shrink-0 md:hidden" />
            <span className="text-sm font-medium text-foreground md:text-muted-foreground/60">
              {APP_DISPLAY_NAME}
            </span>
          </div>
        </header>

        <Empty className="flex-1">
          <div className="w-full max-w-xl rounded-3xl border border-border/55 bg-card/20 px-8 py-12 shadow-sm/5">
            <EmptyHeader className="max-w-none">
              <div className="mx-auto mb-5 flex size-11 items-center justify-center rounded-xl border border-border/70 bg-background/70 text-muted-foreground">
                <LinkIcon className="size-5" />
              </div>
              <EmptyTitle className="text-foreground text-xl">
                <Trans>Connect an environment to get started</Trans>
              </EmptyTitle>
              <EmptyDescription className="mt-2 text-sm leading-relaxed text-muted-foreground/78">
                <Trans>
                  Open a pairing link or add a reachable backend manually. Your saved environments
                  stay in this browser.
                </Trans>
              </EmptyDescription>
              <div className="mt-6 flex justify-center">
                <Button render={<Link to="/settings/connections" />} size="sm">
                  <PlusIcon className="size-4" />
                  <Trans>Add environment</Trans>
                </Button>
              </div>
            </EmptyHeader>
          </div>
        </Empty>
      </div>
    </SidebarInset>
  );
}
